import api from '@/lib/api';
import type { PaginatedResponse } from '@pms/shared';

export interface GroupBooking {
  id: string;
  tenantId: string;
  propertyId: string;
  name: string;
  companyId: string | null;
  contactGuestId: string | null;
  checkIn: string;
  checkOut: string;
  status: 'tentative' | 'confirmed' | 'checked_in' | 'completed' | 'cancelled';
  totalUnits: number;
  totalAmount: string;
  currency: string;
  notes: string | null;
  source: string | null;
  createdAt: string;
  updatedAt: string;
  bookings?: Array<{
    id: string;
    unitId: string;
    status: string;
    checkIn: string;
    checkOut: string;
    guestsCount: number;
    totalAmount: string;
    specialRequests: string | null;
    unit?: { id: string; name: string; type: string } | null;
    guest?: { id: string; fullName: string; email: string | null } | null;
  }>;
  company?: { id: string; name: string } | null;
  contactGuest?: { id: string; fullName: string; email: string | null } | null;
}

export interface GroupBookingsQuery {
  propertyId?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export interface CreateGroupBookingPayload {
  name: string;
  propertyId: string;
  checkIn: string;
  checkOut: string;
  units: Array<{ unitId: string; guestsCount: number; specialRequests?: string }>;
  companyId?: string;
  contactGuestId?: string;
  notes?: string;
  source?: string;
}

export interface UpdateGroupBookingPayload {
  name?: string;
  status?: string;
  companyId?: string;
  notes?: string;
}

export async function fetchGroupBookings(query: GroupBookingsQuery): Promise<PaginatedResponse<GroupBooking>> {
  const { data } = await api.get<PaginatedResponse<GroupBooking>>('/bookings/groups', { params: query });
  return data;
}

export async function fetchGroupBooking(id: string): Promise<GroupBooking> {
  const { data } = await api.get<GroupBooking>(`/bookings/groups/${id}`);
  return data;
}

export async function createGroupBooking(payload: CreateGroupBookingPayload): Promise<GroupBooking> {
  const { data } = await api.post<GroupBooking>('/bookings/groups', payload);
  return data;
}

export async function updateGroupBooking(id: string, payload: UpdateGroupBookingPayload): Promise<GroupBooking> {
  const { data } = await api.patch<GroupBooking>(`/bookings/groups/${id}`, payload);
  return data;
}

export async function confirmGroupBooking(id: string): Promise<GroupBooking> {
  const { data } = await api.post<GroupBooking>(`/bookings/groups/${id}/confirm`);
  return data;
}

export async function cancelGroupBooking(id: string, reason?: string): Promise<GroupBooking> {
  const { data } = await api.post<GroupBooking>(`/bookings/groups/${id}/cancel`, { reason });
  return data;
}
